import { TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorIndicator from 'src/components/atoms/error-indicator';
import { Status } from 'src/components/atoms/status';
import { SpaceType } from 'src/features/booking-rules/api/types/response/booking-rule.response';
import { spaceTypesTranslations } from 'src/features/booking-rules/constants/space-types-translations';
import { getBooleanStatusColor } from 'src/utils/get-boolean-status-color';
import { getBooleanStatusTranslation } from 'src/utils/get-boolean-status-translation';

export interface EmployeesGroupActionsSlotPayload {
  token: string;
  isDefault: boolean;
}

interface Props {
  token: string;
  name: string;
  spaceGroupsNames: string[];
  bookingRules: { name: string; spaceType: SpaceType }[];
  numberOfEmployees: number;
  isDefault: boolean;
  actionsSlot: (payload: EmployeesGroupActionsSlotPayload) => ReactNode;
}

export const EmployeesGroupsTableRow = ({
  token,
  name,
  spaceGroupsNames,
  bookingRules,
  numberOfEmployees,
  isDefault,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation(['bookingRules', 'employeesGroups', 'common']);

  const navigate = useNavigate();

  const renderSpaceGroupsNames = useMemo(() => {
    switch (true) {
      case spaceGroupsNames.length === 1:
        return <Typography variant="body2">{spaceGroupsNames[0]}</Typography>;

      case spaceGroupsNames.length > 1:
        return (
          <Tooltip
            title={spaceGroupsNames.map(groupName => (
              <Typography key={groupName} variant="body2">
                {groupName}
              </Typography>
            ))}>
            <span>
              <Typography
                variant="body2"
                sx={{
                  textDecoration: 'underline',
                }}>{`${spaceGroupsNames.length} ${t('employeesGroups:common.table.groups')}`}</Typography>
            </span>
          </Tooltip>
        );

      default:
        return (
          <Typography variant="body2" color="textSecondary">
            {t('common:notAssigned')}
          </Typography>
        );
    }
  }, [spaceGroupsNames, t]);

  const renderBookingRules = useMemo(() => {
    switch (true) {
      case bookingRules.length === 1:
        return (
          <Typography variant="body2">
            {`${bookingRules[0].name} (${t(spaceTypesTranslations[bookingRules[0].spaceType])})`}
          </Typography>
        );

      case bookingRules.length > 1:
        return (
          <Tooltip
            title={bookingRules.map(rule => (
              <Typography key={rule.spaceType} variant="body2">
                {`${rule.name} (${t(spaceTypesTranslations[rule.spaceType])})`}
              </Typography>
            ))}>
            <span>
              <Typography
                variant="body2"
                sx={{
                  textDecoration: 'underline',
                }}>{`${bookingRules.length} ${t('employeesGroups:common.table.bookingRules')}`}</Typography>
            </span>
          </Tooltip>
        );

      default:
        return (
          <Typography variant="body2" color="textSecondary">
            {t('common:notAssigned')}
          </Typography>
        );
    }
  }, [bookingRules, t]);

  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer' }}
      onClick={() =>
        navigate({
          to: '/tenant/employees/groups/$groupToken/employees',
          params: {
            groupToken: token,
          },
        })
      }>
      <TableCell width={50}>
        <ErrorIndicator
          tooltipTitle={t(
            'employeesGroups:common.table.noSpaceGroupOrBookingRulesTooltip',
          )}
          visible={spaceGroupsNames.length === 0 || bookingRules.length === 0}
          color="warning.secondary"
        />
      </TableCell>

      <TableCell>
        <Typography variant="body2">{name}</Typography>
      </TableCell>

      <TableCell>{renderSpaceGroupsNames}</TableCell>

      <TableCell>{renderBookingRules}</TableCell>

      <TableCell>
        <Typography variant="body2">{numberOfEmployees}</Typography>
      </TableCell>

      <TableCell>
        <Status
          label={t(getBooleanStatusTranslation(isDefault))}
          color={getBooleanStatusColor(isDefault)}
        />
      </TableCell>

      <TableCell align="right" sx={{ cursor: 'default' }}>
        {actionsSlot({ token, isDefault })}
      </TableCell>
    </TableRow>
  );
};
